import { z } from "zod";
import { coreAttributionSchema, setOriginSchema } from "./core-library";
import { examCodeSchema, programmeCodeSchema } from "./programmes";
import { subscriptionSchema } from "./billing";
import { sourceSchema } from "./sources";

/** Set lifecycle (spec §5.2 `sets`). Counts shown to students include published items only (§6.4). */
export const SET_STATUSES = ["draft", "generating", "ready", "failed", "archived"] as const;
export const setStatusSchema = z.enum(SET_STATUSES);
export type SetStatus = z.infer<typeof setStatusSchema>;

export const setSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: setStatusSchema,
  programme: programmeCodeSchema,
  exam: examCodeSchema,
  question_count: z.number().int().nonnegative(),
  card_count: z.number().int().nonnegative(),
  source_label: z.string(),
  created_at: z.string(),
  is_owner: z.boolean(),
  /** "core" = a StudyWard Core library set (read-only, cited to open textbooks). */
  origin: setOriginSchema.default("user"),
});
export type SetSummary = z.infer<typeof setSummarySchema>;

export const setListResponseSchema = z.object({ items: z.array(setSummarySchema), next_cursor: z.string().nullable() });

/** Share of the set's items per blueprint category — doubles as a coverage audit (mock screen 9). */
export const topicShareSchema = z.object({ key: z.string(), name: z.string(), items: z.number().int().nonnegative() });

export const setDetailSchema = setSummarySchema.extend({
  exam_name: z.string(),
  config_version: z.string(),
  error_message: z.string().nullable(),
  quarantined_count: z.number().int().nonnegative(),
  chunk_count: z.number().int().nonnegative(),
  topics: z.array(topicShareSchema),
  sources: z.array(sourceSchema),
  job_id: z.string().uuid().nullable(),
  /** From the exam's format pack: drives the default timer in quiz setup (§10.1 #29). */
  pace_seconds_per_item: z.number().positive(),
  /** Core library sets: the licensed source and its licence, shown to students (plan §5.2a). */
  attribution: coreAttributionSchema.nullable().default(null),
});
export type SetDetail = z.infer<typeof setDetailSchema>;

export const createSetRequestSchema = z.object({
  source_ids: z.array(z.string().uuid()).min(1).max(10),
  title: z.string().trim().min(1).max(120).optional(),
  /** Client-generated per tap; resubmitting returns the same job and never charges twice (§5.4). */
  idempotency_key: z.string().min(8).max(80),
});
export type CreateSetRequest = z.infer<typeof createSetRequestSchema>;

export const setPatchSchema = z
  .object({ title: z.string().trim().min(1).max(120), archived: z.boolean() })
  .partial()
  .refine((p) => Object.keys(p).length > 0, "Nothing to update");

/**
 * Pipeline stages (spec §6.2), one per worker invocation (§3). `regenerate` and the second `verify` round
 * only run when the first verification sends items back.
 */
export const JOB_STAGES = ["ingest", "chunk", "generate_items", "generate_notes", "verify", "regenerate", "publish", "done"] as const;
export const jobStageSchema = z.enum(JOB_STAGES);
export type JobStage = z.infer<typeof jobStageSchema>;

export const JOB_STATUSES = ["queued", "running", "succeeded", "failed"] as const;
export const jobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof jobStatusSchema>;

/** The five named steps the Generating screen shows (mock screen 8), in order. */
export const GENERATION_STEPS = [
  { key: "read", title: "Reading your material", detail: "Text, tables and slides extracted page by page" },
  { key: "concepts", title: "Finding the testable concepts", detail: "Split into cited sections of your material" },
  { key: "write", title: "Writing exam-style questions", detail: "In your exam's own question formats" },
  { key: "cite", title: "Attaching source citations", detail: "Every item points to its page or slide" },
  { key: "check", title: "Checking answers against the source", detail: "Unsupported answers are discarded, not shipped" },
] as const;

/** How many of the five steps are complete at a stage. */
export function completedSteps(stage: JobStage): number {
  switch (stage) {
    case "ingest":
      return 0;
    case "chunk":
      return 1;
    case "generate_items":
      return 2;
    case "generate_notes":
      return 3;
    case "verify":
    case "regenerate":
      return 4;
    case "publish":
    case "done":
      return 5;
  }
}

export const jobSchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  status: jobStatusSchema,
  stage: jobStageSchema,
  completed_steps: z.number().int().min(0).max(5),
  /** Set while a queued job is waiting: for the shared free AI quota (§5.4) or a retry after an error. */
  waiting_until: z.string().nullable(),
  waiting_reason: z.enum(["free_quota", "retry"]).nullable(),
  error_message: z.string().nullable(),
  can_retry: z.boolean(),
});
export type Job = z.infer<typeof jobSchema>;

/**
 * POST /sets/:id/gap-fill (plan §7.5, BE20): more questions for a ready set, aimed at blueprint categories the set
 * covers least (or the ones named), from that set's own material only. Counts as one generation this month.
 */
export const GAP_FILL_QUESTIONS = { min: 5, max: 15, default: 10 } as const;
export const gapFillRequestSchema = z.object({
  idempotency_key: z.string().min(8).max(80),
  categories: z.array(z.string().min(1).max(60)).max(3).optional(),
  questions: z.number().int().min(GAP_FILL_QUESTIONS.min).max(GAP_FILL_QUESTIONS.max).default(GAP_FILL_QUESTIONS.default),
});
export type GapFillRequest = z.infer<typeof gapFillRequestSchema>;

/** Categories a set covers least relative to their blueprint weight (ties: heavier category first). */
export function underCoveredCategories(categories: Array<{ key: string; weight: number }>, counts: Record<string, number>, take: number): string[] {
  const total = Object.values(counts).reduce((n, c) => n + c, 0);
  const weightSum = categories.reduce((n, c) => n + c.weight, 0) || 1;
  return [...categories]
    .map((c) => ({ key: c.key, weight: c.weight, gap: (c.weight / weightSum) * Math.max(total, 1) - (counts[c.key] ?? 0) }))
    .sort((a, b) => b.gap - a.gap || b.weight - a.weight)
    .slice(0, take)
    .map((c) => c.key);
}

export const createSetResponseSchema = z.object({ set_id: z.string().uuid(), job_id: z.string().uuid().nullable(), reused: z.boolean() });

/** GET /billing/entitlement — plan plus this period's usage, so limit states show real numbers (§8.4). */
export const entitlementUsageSchema = z.object({
  plan: z.enum(["free", "pro"]),
  period: z.string(),
  resets_at: z.string(),
  sets: z.object({ used: z.number().int().nonnegative(), allowed: z.number().int().nonnegative().nullable() }),
  voice_minutes: z.object({ used: z.number().int().nonnegative(), allowed: z.number().int().nonnegative().nullable() }),
  /** The cached RevenueCat (or admin-comp) subscription; null when the user has never had Pro (§9). */
  subscription: subscriptionSchema.nullable(),
});
export type EntitlementUsage = z.infer<typeof entitlementUsageSchema>;
